import { useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { useForm } from 'hooks/useForm';
import { transferMoney } from '@redux/features/cashboxesSlice';
import { getTransferSchema } from 'constants/formValidationSchemas/transferSchema';
import { CASHBOX_TYPES } from 'constants/cashboxTypes';
import { MoneyAmount } from 'components/MoneyAmount';
import { Dropdown } from 'components/Dropdown';
import { Button } from 'components/Button';
import { Input } from 'components/Input';
import { Modal } from 'components/Modal';
import styles from '../Cashboxes.scss';

export const CreateTransferForm = ({ cashboxes, isOpen, setIsOpen, initialFromCashbox, hideFromField = false }) => {
  const dispatch = useDispatch();

  const formatCurrency = (amount) =>
    new Intl.NumberFormat('uk-UA', {
      style: 'currency',
      currency: 'UAH',
      minimumFractionDigits: 0,
    }).format(amount);

  const handleTransferClose = () => {
    setIsOpen(false);
    resetForm();
  };

  const onSubmit = async (values) => {
    const resultAction = await dispatch(
      transferMoney({
        from: parseInt(values.from.value),
        to: parseInt(values.to.value),
        amount: parseInt(values.amount),
      })
    );

    if (transferMoney.fulfilled.match(resultAction)) {
      handleTransferClose();
    }
  };

  const { values, errors, touched, handleChange, handleBlur, handleSubmit, setFieldValue, resetForm, isValid } =
    useForm({
      initialValues: { from: null, to: null, amount: '' },
      validationSchema: getTransferSchema(cashboxes),
      onSubmit,
      trimValues: true,
      status: {},
    });

  useEffect(() => {
    if (initialFromCashbox && isOpen) {
      setFieldValue('from', {
        label:
          initialFromCashbox.type === CASHBOX_TYPES.SAFE
            ? `Сейф ${initialFromCashbox.location.name} (${formatCurrency(initialFromCashbox.balance)})`
            : `${initialFromCashbox.name} (${formatCurrency(initialFromCashbox.balance)})`,
        value: initialFromCashbox.id,
      });
    }
  }, [isOpen, initialFromCashbox]);

  useEffect(() => {
    if (values.from?.value === values.to?.value) {
      setFieldValue('to', null);
    }
  }, [values.from]);

  const fromOptions = useMemo(
    () =>
      cashboxes.map((c) => ({
        label:
          c.type === CASHBOX_TYPES.SAFE
            ? `Сейф ${c.location.name} (${formatCurrency(c.balance)})`
            : `${c.name} (${formatCurrency(c.balance)})`,
        value: c.id,
      })),
    [cashboxes]
  );

  const toOptions = useMemo(
    () =>
      cashboxes
        .filter((c) => c.id !== values.from?.value)
        .map((c) => ({
          label: c.type === CASHBOX_TYPES.SAFE ? `Сейф ${c.location.name}` : c.name,
          value: c.id,
        })),
    [cashboxes, values.from]
  );

  return (
    <Modal isOpen={isOpen} setIsOpen={handleTransferClose}>
      <h3>Переказ між рахунками</h3>

      <form onSubmit={handleSubmit}>
        {!hideFromField && (
          <div className={styles.formGroup}>
            <label>З рахунку:</label>
            <Dropdown
              value={
                values.from && {
                  ...values.from,
                  label: values.from.label?.split('(')[0],
                }
              }
              options={fromOptions}
              placeholder='Оберіть рахунок'
              onChange={(value) => setFieldValue('from', value)}
              invalid={touched.from && errors.from}
              errorText={touched.from && errors.from}
              clearable={false}
            />
          </div>
        )}

        <div className={styles.formGroup}>
          <label>На рахунок:</label>
          <Dropdown
            value={values.to}
            options={toOptions}
            placeholder='Оберіть рахунок'
            onChange={(value) => setFieldValue('to', value)}
            invalid={touched.to && errors.to}
            errorText={touched.to && errors.to}
            clearable={false}
          />
        </div>

        <div className={styles.formGroup}>
          <label>Сума (₴):</label>
          <Input
            type='number'
            min='1'
            name='amount'
            value={values.amount}
            onChange={handleChange}
            onBlur={handleBlur}
            placeholder='Введіть суму'
            invalid={touched.amount && errors.amount}
            errorText={touched.amount && errors.amount}
          />
          <div>
            Доступно: <MoneyAmount value={cashboxes.find((c) => c.id === parseInt(values.from?.value))?.balance || 0} />
          </div>
        </div>

        <div className={styles.modalActions}>
          <Button className={styles.modalBtn} type='button' onClick={handleTransferClose} ghostStyle smallStyle>
            Скасувати
          </Button>
          <Button className={styles.modalBtn} type='submit' disabled={!isValid} smallStyle>
            Підтвердити переказ
          </Button>
        </div>
      </form>
    </Modal>
  );
};
